import React from 'react';
import { Box, Text, VStack, HStack, Button, Icon, SimpleGrid } from '@chakra-ui/react';
import { FiSettings, FiAlertCircle } from 'react-icons/fi';
import Card from 'components/card/Card';
import EmbeddedSignupButton from './EmbeddedSignupButton';
import ConnectMetaAppForm from './ConnectMetaAppForm';
import { REFUSAL_TEXT } from './_shared';

/**
 * The entry point: how a tenant gets a WhatsApp number into this CRM.
 *
 * Two doors. "Connect with Facebook" is Meta's Embedded Signup on the
 * platform's app — one login, no webhooks to configure — and exists only on an
 * installation whose operator registered such an app. "Use your own Meta app"
 * is always there: the tenant pastes the ids and a token from their own app,
 * then points that app's webhook at this CRM with the values shown beside the
 * form. Both end in the same place — a number in the list below, assignable
 * to agents.
 */
export default function ConnectionPanel({ setup, onConnected, reconnect = null }) {
	const signup = setup?.embeddedSignup;
	const signupAvailable = Boolean(signup?.available && signup?.exchangeUrl);
	// With no platform app the form is the only door: open it straight away.
	const [ownApp, setOwnApp] = React.useState(false);
	// ★ A RECONNECT FORCES THE FORM OPEN. Embedded Signup cannot re-seal a
	// token for a workspace that connected its own Meta app — that token comes
	// from their app, not the platform's — so a reconnect always lands on the
	// form, whichever door is otherwise offered.
	const showForm = Boolean(reconnect) || ownApp || (setup && !signupAvailable);

	// Nothing can be connected until the two services are wired together. The
	// checklist above this panel already names every missing variable, so this
	// says only what it adds: the doors are closed for now.
	if (setup && setup.available === false) {
		return (
			<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
				<HStack spacing="3" align="start">
					<Icon as={FiAlertCircle} color="text.muted" mt="0.5" />
					<Box>
						<Text fontSize="sm" fontWeight="medium" color="text.primary">
							Connecting a number is unavailable until the checklist above is clear
						</Text>
						<Text fontSize="xs" color="text.muted" mt="0.5">
							{REFUSAL_TEXT[setup.reason] ?? 'The WhatsApp connection service is not available yet.'}
						</Text>
					</Box>
				</HStack>
			</Card>
		);
	}

	return (
		<VStack align="stretch" spacing="4">
			<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
				<Box>
					<Text fontSize="sm" fontWeight="bold" color="text.primary">
						Connect a WhatsApp number
					</Text>
					<Text fontSize="xs" color="text.muted" mt="0.5" mb="4">
						Each workspace connects its own WhatsApp Business Account. Pick how you want to connect.
					</Text>

					<SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
						<Box p="3" borderWidth="1px" borderColor="border.subtle" borderRadius="lg" bg="bg.subtle">
							<Text fontSize="sm" fontWeight="medium" color="text.primary" mb="1">
								Connect with Facebook
							</Text>
							<Text fontSize="xs" color="text.muted" mb="3" minH="2.4em">
								Log in with the Facebook account that manages your business. Meta sets up the webhook for
								you.
							</Text>
							<EmbeddedSignupButton signup={signup} onConnected={onConnected} size="sm" />
						</Box>

						<Box
							p="3"
							borderWidth="1px"
							borderColor={showForm ? 'border.focus' : 'border.subtle'}
							borderRadius="lg"
							bg="bg.subtle"
						>
							<Text fontSize="sm" fontWeight="medium" color="text.primary" mb="1">
								Use your own Meta app
							</Text>
							<Text fontSize="xs" color="text.muted" mb="3" minH="2.4em">
								You created an app at developers.facebook.com. Enter two IDs from it, then point the app's
								webhook at this CRM. Your token comes from Admin Settings.
							</Text>
							<Button
								size="sm"
								variant={showForm ? 'solid' : 'outline'}
								colorScheme="green"
								leftIcon={<Icon as={FiSettings} />}
								onClick={() => setOwnApp((v) => !v)}
								isDisabled={!signupAvailable && showForm}
							>
								{showForm ? 'Setting up below' : 'Set up with my app'}
							</Button>
						</Box>
					</SimpleGrid>
				</Box>
			</Card>

			{/* ★ THE WEBHOOK CARD IS NOT HERE ANY MORE.
			    It is the step AFTER connecting, not one of the two doors, and it
			    stays relevant long after this panel is collapsed — so the screen
			    owns it and keeps it beside the numbers. */}
			{showForm && <ConnectMetaAppForm onConnected={onConnected} prefill={reconnect} />}
		</VStack>
	);
}
